import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, Grid, Typography, Chip, Avatar, Button, makeStyles } from "@material-ui/core";
import { WhatsApp, PeopleOutline, ForumOutlined, HourglassEmpty, CheckCircleOutline } from "@material-ui/icons";
import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import Chart from "./Chart";

const labels = { CONNECTED: "Conectado", DISCONNECTED: "Desconectado", qrcode: "Aguardando QR Code",
  OPENING: "Iniciando", PAIRING: "Autenticando", TIMEOUT: "Tempo esgotado" };
const useStyles = makeStyles(theme => ({
  root: { padding: 8, minWidth: 0 },
  heading: { fontWeight: 700, marginBottom: 8 },
  subtitle: { marginBottom: 20, color: theme.palette.text.secondary },
  card: { padding: 20, height: "100%", border: "1px solid " + theme.palette.divider, boxShadow: "none", borderRadius: 14 },
  metrics: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 14, marginBottom: 20,
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    "& > :last-child:nth-child(odd)": { [theme.breakpoints.down("md")]: { gridColumn: "1 / -1" } },
  },
  metricCard: { padding: 18, borderRadius: 18, minWidth: 0, border: "1px solid " + theme.palette.divider,
    "&:nth-child(1)": { background: theme.app.gradient, color: "#ffffff", borderColor: "transparent" },
    "&:nth-child(2)": { background: theme.app.waiting, color: theme.app.waitingInk },
    "&:nth-child(3)": { background: theme.app.tint, color: theme.palette.primary.main },
    "&:nth-child(4)": { background: theme.app.blue, color: theme.app.blueInk },
    "&:nth-child(5)": { background: theme.app.violet, color: theme.app.violetInk },
    "& $icon": { color: "inherit", background: "rgba(255,255,255,.16)", padding: 7 },
    [theme.breakpoints.down("xs")]: { padding: 14, "& $metric": { gap: 4 }, "& .MuiTypography-body2": { fontSize: 12 } },
  },
  metric: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  number: { fontSize: 30, fontWeight: 700, marginTop: 12 },
  icon: { padding: 10, display: "flex", borderRadius: 12, color: theme.palette.primary.main, background: theme.app.tint },
  section: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid " + theme.palette.divider, "&:last-child": { borderBottom: 0 } },
  name: { flex: 1, minWidth: 0, overflowWrap: "anywhere" },
  empty: { padding: "24px 0", color: theme.palette.text.secondary },
  chart: { height: 310, minWidth: 0, padding: 20, border: "1px solid " + theme.palette.divider, boxShadow: "none", borderRadius: 14 },
}));

export default function Dashboard() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps = [], loading: connectionsLoading } = useContext(WhatsAppsContext);
  const queueIds = JSON.stringify((user.queues || []).map(q => q.id));
  const { count: open } = useTickets({ status: "open", showAll: "true", withUnreadMessages: "false", queueIds });
  const { count: pending } = useTickets({ status: "pending", showAll: "true", withUnreadMessages: "false", queueIds });
  const { count: closed } = useTickets({ status: "closed", showAll: "true", withUnreadMessages: "false", queueIds });
  const [online, setOnline] = useState(null);
  const [presenceError, setPresenceError] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  const isAdmin = user.profile === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const { data } = await api.get("/dashboard/online-users");
        if (active) { setOnline(data.users); setPresenceError(false); setCheckedAt(data.checkedAt); }
      } catch (error) { if (active) setPresenceError(true); }
      finally { if (active) timer = setTimeout(refresh, 15000); }
    };
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [isAdmin]);
  const connected = whatsApps.filter(w => w.status === "CONNECTED").length;
  const metrics = [
    ["Em atendimento", open, <ForumOutlined />],
    ["Aguardando", pending, <HourglassEmpty />],
    ["Finalizados", closed, <CheckCircleOutline />],
    ["WhatsApp conectado", connectionsLoading ? "…" : connected, <WhatsApp />],
    ...(isAdmin ? [["Usuários online", presenceError ? "—" : online === null ? "…" : online.length, <PeopleOutline />]] : []),
  ];
  return <div className={classes.root}>
    <Typography component="h1" variant="h5" className={classes.heading}>Visão geral</Typography>
    <Typography className={classes.subtitle}>Acompanhe os atendimentos, as conexões e sua equipe.</Typography>
    <div className={classes.metrics}>
      {metrics.map(([title, value, icon]) => <Paper key={title} className={classes.metricCard}><div className={classes.metric}>
          <Typography variant="body2">{title}</Typography><span className={classes.icon}>{icon}</span>
        </div><Typography className={classes.number}>{value ?? "…"}</Typography></Paper>)}
    </div>
    <Grid container spacing={2}>
      <Grid item xs={12} md={isAdmin ? 7 : 12}>
        <Paper className={classes.card}>
          <div className={classes.section}><Typography component="h2" variant="h6">Contas do WhatsApp</Typography>
            <Button component={RouterLink} to="/connections" color="primary">Ver conexões</Button></div>
          {connectionsLoading && <Typography className={classes.empty}>Carregando conexões…</Typography>}
          {!connectionsLoading && !whatsApps.length && <Typography className={classes.empty}>Nenhuma conta cadastrada.</Typography>}
          {whatsApps.map(w => <div className={classes.row} key={w.id}>
            <Avatar className={classes.icon}><WhatsApp /></Avatar>
            <div className={classes.name}><Typography style={{ fontWeight: 600 }}>{w.name}</Typography>
              <Typography variant="caption" color="textSecondary">{w.isDefault ? "Conexão padrão" : "Conta de atendimento"}</Typography></div>
            <Chip size="small" variant={w.status === "CONNECTED" ? "default" : "outlined"} color={w.status === "CONNECTED" ? "primary" : "default"}
              label={labels[w.status] || w.status || "Estado desconhecido"} />
          </div>)}
        </Paper>
      </Grid>
      {isAdmin && <Grid item xs={12} md={5}>
        <Paper className={classes.card}>
          <Typography component="h2" variant="h6">Equipe online</Typography>
          <Typography variant="caption" color="textSecondary">Sessões abertas no sistema · atualização a cada 15 segundos</Typography>
          {presenceError ? <Typography role="status" className={classes.empty}>Não foi possível atualizar a presença. Tentando novamente…</Typography>
            : online === null ? <Typography className={classes.empty}>Carregando equipe…</Typography>
            : !online.length ? <Typography className={classes.empty}>Nenhum usuário online neste momento.</Typography>
            : online.map(member => <div className={classes.row} key={member.id}>
                <Avatar>{member.name?.slice(0, 1).toUpperCase()}</Avatar>
                <Typography className={classes.name}>{member.name}</Typography>
                <Chip size="small" color="primary" label="Online" />
              </div>)}
          {checkedAt && !presenceError && <Typography variant="caption" color="textSecondary">Atualizado às {new Date(checkedAt).toLocaleTimeString("pt-BR")}</Typography>}
        </Paper>
      </Grid>}
      <Grid item xs={12}><Paper className={classes.chart}><Chart /></Paper></Grid>
    </Grid>
  </div>;
}

